import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from './supabase';

interface User {
  id: string;
  phone: string;
  user_type: 'Admin' | 'User';
  name: string;
}

interface AuthContextType {
  user: User | null;
  login: (phone: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in on app start
    const authToken = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    
    if (authToken && userData) {
      try {
        const user = JSON.parse(userData);
        setUser(user);
        setIsAuthenticated(true);
        console.log('🔐 Restored user session:', user.phone);
      } catch (error) {
        // Invalid user data, clear storage
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        console.log('🔐 Cleared invalid session data');
      }
    }

    // Since we're using custom authentication, we don't need to listen to Supabase auth state changes
    // The Supabase session clearing was causing unwanted logouts on refresh
    // Our authentication is based on localStorage tokens, not Supabase auth sessions
    console.log('🔐 AuthContext initialized with custom authentication');
    
    // Set loading to false after checking authentication
    setIsLoading(false);
  }, []);

  const login = async (phone: string, password: string): Promise<boolean> => {
    try {
      if (!supabase) {
        console.error('Supabase not configured');
        return false;
      }

      console.log('🔐 Attempting login with phone:', phone);

      // First, verify the user credentials using our RPC
      const { data: authResult, error: rpcError } = await supabase
        .rpc('verify_user_password', {
          user_phone: phone,
          user_password: password
        });

      if (rpcError) {
        console.error('❌ RPC Authentication error:', rpcError);
        return false;
      }

      console.log('🔐 RPC Auth result:', authResult);

      if (authResult?.success) {
        const userId = authResult.user_data.id;
        console.log('✅ User verified, ID:', userId);

        // Now authenticate with Supabase using the user ID
        // We'll create a temporary JWT token or use signInAnonymously and then set the user context
        
        // Method 1: Try to sign in with a generated email based on phone
        const email = `${phone}@beautello.app`;
        console.log('🔐 Attempting Supabase auth with email:', email);
        
        // First try to sign in
        let supabaseUser;
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: email,
          password: password
        });

        if (signInError) {
          console.log('🔐 Sign in failed, trying to create user:', signInError.message);
          
          // If sign in fails, try to create the user
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: email,
            password: password,
            options: {
              data: {
                phone: phone,
                user_id: userId,
                user_type: authResult.user_data.user_type
              }
            }
          });

          if (signUpError) {
            console.error('❌ Sign up failed:', signUpError);
            // Fallback: continue without Supabase auth but store user info
            console.log('⚠️ Continuing without Supabase auth session');
          } else {
            console.log('✅ User created in Supabase auth:', signUpData.user?.id);
            supabaseUser = signUpData.user;
          }
        } else {
          console.log('✅ User signed in to Supabase auth:', signInData.user?.id);
          supabaseUser = signInData.user;
        }

        // Create user info object
        const userInfo = {
          id: userId, // Use the database user ID, not Supabase auth ID
          phone: authResult.user_data.phone,
          user_type: authResult.user_data.user_type === 'admin' ? 'Admin' as const : 'User' as const,
          name: authResult.user_data.user_type === 'admin' ? 'Admin User' : 'User',
          supabaseAuthId: supabaseUser?.id || null
        };
        
        console.log('✅ Final user info:', userInfo);
        
        // Store auth token and user info
        localStorage.setItem('authToken', `supabase-${userId}`);
        localStorage.setItem('user', JSON.stringify(userInfo));
        
        setUser(userInfo);
        setIsAuthenticated(true);
        return true;
      } else {
        console.error('❌ Invalid phone or password:', authResult?.error);
        return false;
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    // Sign out from Supabase auth
    if (supabase) {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Supabase sign out error:', error);
      } else {
        console.log('✅ Signed out from Supabase auth');
      }
    }
    
    // Clear auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    // Update state
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 